import {
    Booking,
    Customer,
    GetAvailableDatesResponse,
    PagedResponse,
    ResDiaryApiService,
    RestApiOptions,
} from './types';
import { createResDiaryUniversalRequest, parseProxyResponse } from './universalProxyRequest';
import { extendUrlWithQueryParameters } from './utils';

const availabilityType = 'Reservation';
const channelCode = 'PMS';
const isLeaveTimeConfirmed = true;
const pageSize = 100;

export const createApiService = (options: RestApiOptions): ResDiaryApiService => {
    const universalProxyRequest = createResDiaryUniversalRequest(options);

    return {
        cancelBooking: async ({ restaurant, bookingReference, cancellationReasonId }) =>
            universalProxyRequest(
                extendUrlWithQueryParameters(`Restaurant/${restaurant}/Booking/${bookingReference}/Cancel`, {
                    cancellationReasonId,
                }),
                {},
                { method: 'POST' }
            ),

        createBooking: async ({
            areaId,
            customer,
            partySize,
            promotionId,
            reservationId,
            restaurant,
            roomNumber,
            specialRequests,
            visitDate,
            visitTime,
        }) =>
            options
                .request('/resDiary/createBooking', {
                    body: JSON.stringify({
                        areaId,
                        channelCode,
                        customer,
                        isLeaveTimeConfirmed,
                        partySize,
                        promotionId,
                        roomNumber,
                        specialRequests,
                        visitDate,
                        visitTime,
                    }),
                    micrositeName: restaurant,
                    reservationId,
                })
                .then(parseProxyResponse),

        findBookings: async ({ customerId, restaurant, visitDateFrom }) =>
            universalProxyRequest(
                extendUrlWithQueryParameters(`Restaurant/${restaurant}/Bookings`, {
                    customerId,
                    visitDateFrom,
                    pageSize,
                    sortBy: 'VisitDateTimeUtc',
                }),
                null,
                { method: 'GET' }
            ).then(({ data }: PagedResponse<Array<Booking>>) => data),

        findCustomers: async ({ email, firstName, restaurant, surname }) =>
            universalProxyRequest(
                extendUrlWithQueryParameters(`Restaurant/${restaurant}/Customers`, { email, surname, firstName }),
                null,
                { method: 'GET' }
            ).then(({ data }: PagedResponse<Array<Customer>>) => data),

        getAvailableDates: async ({ dateFrom, dateTo, partySize, restaurant }) =>
            universalProxyRequest(
                `Restaurant/${restaurant}/AvailabilityForDateRangeV2`,
                { availabilityType, dateFrom, dateTo, partySize },
                { method: 'POST' }
            ).then(({ availableDates }: GetAvailableDatesResponse) => availableDates),

        getBooking: async ({ restaurant, bookingReference }) =>
            universalProxyRequest(`Restaurant/${restaurant}/Booking/${bookingReference}`, null, { method: 'GET' }),

        getCancellationReasons: async ({ restaurant }) =>
            universalProxyRequest(`Restaurant/${restaurant}/CancellationReasons`, null, { method: 'GET' }),

        getRestaurants: async () => universalProxyRequest('Restaurants', null, { method: 'GET' }),

        getSetup: async ({ restaurant, date }) =>
            universalProxyRequest(
                extendUrlWithQueryParameters(`Restaurant/${restaurant}/Setup`, { date, channelCode }),
                null,
                {
                    method: 'GET',
                }
            ),

        oldCreateBooking: async ({
            areaId,
            customer,
            partySize,
            promotionId,
            restaurant,
            roomNumber,
            specialRequests,
            visitDate,
            visitTime,
        }) =>
            universalProxyRequest(
                `Restaurant/${restaurant}/BookingWithStripeToken`,
                {
                    areaId,
                    channelCode,
                    customer,
                    isLeaveTimeConfirmed,
                    partySize,
                    promotionId,
                    roomNumber,
                    specialRequests,
                    visitDate,
                    visitTime,
                },
                { method: 'POST' }
            ),

        searchAvailability: async ({ areaId, bookingId, partySize, restaurant, visitDate }) =>
            universalProxyRequest(
                `Restaurant/${restaurant}/AvailabilitySearch`,
                { areaId, availabilityType, bookingId, channelCode, partySize, visitDate },
                { method: 'POST' }
            ),

        updateBooking: async ({
            areaId,
            bookingReference,
            partySize,
            promotionId,
            restaurant,
            roomNumber,
            specialRequests,
            visitDate,
            visitTime,
        }) =>
            universalProxyRequest(
                `Restaurant/${restaurant}/Booking/${bookingReference}`,
                {
                    areaId,
                    isLeaveTimeConfirmed,
                    partySize,
                    promotionId,
                    roomNumber,
                    specialRequests,
                    visitDate,
                    visitTime,
                },
                { method: 'PUT' }
            ),
    };
};
